import { LocationData, SalesTier } from '../services/DashboardService';
import { DateRange } from '../services/DashboardService';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatCurrency, formatPercent, formatPercentWithSign } from './formatters';

export interface BottomPerformer {
  location: LocationData;
  gfsPercent: number;
  priorWeekSales: number | null;
  ratio: number | null; // GFS% / prior week sales (or 8th week GFS% / prior week sales)
  rank: number;
}

/**
 * Calculates the prior week date range from a given date range
 */
export function getPriorWeekDateRange(currentDateRange: DateRange): DateRange {
  const startDate = new Date(currentDateRange.startDate);
  const endDate = new Date(currentDateRange.endDate);
  
  // Subtract 7 days
  startDate.setDate(startDate.getDate() - 7);
  endDate.setDate(endDate.getDate() - 7);
  
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  return {
    startDate: formatDate(startDate),
    endDate: formatDate(endDate),
    label: `Prior Week`,
    fileName: '',
  };
}

/**
 * Gets bottom 10 performers based on GFS% compared to prior week total sales
 * For weekly view: compares 8th week GFS% to prior week total sales
 * If prior week sales not available, ranks by 8th week GFS% alone
 */
export function getBottomPerformers(
  locations: LocationData[],
  priorWeekSalesByLocation: Map<string, number>,
  useEightWeekGfs: boolean = true
): BottomPerformer[] {
  const performers: BottomPerformer[] = [];
  
  for (const location of locations) {
    // Get the GFS% to use (8th week or current week)
    const gfsPercent = useEightWeekGfs
      ? (location.eightWeekGfsPercentValue ?? 
         (location.eightWeekTotalSales > 0 && location.eightWeekTotalGfs
          ? (location.eightWeekTotalGfs / location.eightWeekTotalSales) * 100
          : null))
      : location.gfsPercent;
    
    if (gfsPercent === null || gfsPercent === undefined || isNaN(gfsPercent) || !isFinite(gfsPercent)) {
      continue;
    }
    
    // Get prior week sales for this location (optional)
    const priorWeekSales = priorWeekSalesByLocation.get(location.location) ?? null;
    
    // Calculate ratio: if prior week sales available, use GFS% / prior week sales
    // Otherwise, just use GFS% as the ranking metric (higher GFS% = worse performer)
    let ratio: number;
    if (priorWeekSales !== null && priorWeekSales > 0) {
      ratio = gfsPercent / priorWeekSales;
    } else {
      // No prior week data - just use GFS% as the metric (higher = worse)
      ratio = gfsPercent;
    }
    
    performers.push({
      location,
      gfsPercent,
      priorWeekSales,
      ratio,
      rank: 0, // Will be set after sorting
    });
  }
  
  // Sort by ratio (highest ratio = worst performer)
  performers.sort((a, b) => {
    if (a.ratio === null && b.ratio === null) return 0;
    if (a.ratio === null) return 1;
    if (b.ratio === null) return -1;
    return b.ratio - a.ratio;
  });
  
  // Assign ranks and return top 10
  return performers.slice(0, 10).map((p, idx) => ({
    ...p,
    rank: idx + 1,
  }));
}

/**
 * Helper to compute 8-week GFS% for a location
 */
function getEightWeekPercent(location: LocationData): number | null {
  if (
    !location.eightWeekTotalSales ||
    location.eightWeekTotalSales <= 0 ||
    location.eightWeekTotalGfs === null ||
    location.eightWeekTotalGfs === undefined
  ) {
    return null;
  }
  return (location.eightWeekTotalGfs / location.eightWeekTotalSales) * 100;
}

/**
 * Exports bottom performers to a PDF grouped by sales tier
 */
export function exportBottomPerformersToPDF(
  performers: BottomPerformer[],
  tiers: SalesTier[],
  filename: string = 'bottom_performers.pdf'
): void {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt' });
  const headers = [
    'Rank',
    'Location',
    'DM',
    'Prior Week Total Sales',
    'Prior Week GFS Spend',
    'Prior Week GFS %',
    'Prior Week GFS Var from Avg',
    '8-Wk Avg Toast',
    '8-Wk Avg Cater',
    '8-Wk Avg Weekly Sales',
    '8-Wk Avg GFS Spend',
    '8-Wk GFS %',
    '8-Wk GFS Var from Avg',
  ];

  doc.setFontSize(16);
  doc.text('Bottom Performers', 40, 40);
  let startY = 60;

  const locationTierMap = new Map<string, string>();
  const tierAveragePercent = new Map<string, number | null>();

  tiers.forEach((tier) => {
    const percents: number[] = [];
    tier.locations.forEach((loc) => {
      locationTierMap.set(loc.location, tier.tierName);
      const pct = getEightWeekPercent(loc);
      if (pct !== null) {
        percents.push(pct);
      }
    });
    const avg = percents.length > 0 ? percents.reduce((sum, value) => sum + value, 0) / percents.length : null;
    tierAveragePercent.set(tier.tierName, avg);
  });

  const grouped = new Map<string, BottomPerformer[]>();
  performers.forEach((p) => {
    const tierName = locationTierMap.get(p.location.location) ?? 'Unassigned';
    if (!grouped.has(tierName)) {
      grouped.set(tierName, []);
    }
    grouped.get(tierName)!.push(p);
  });

  const tierOrder = tiers.map((tier) => tier.tierName);
  if (grouped.has('Unassigned') && !tierOrder.includes('Unassigned')) {
    tierOrder.push('Unassigned');
  }

  tierOrder.forEach((tierName, idx) => {
    const tierPerformers = grouped.get(tierName);
    if (!tierPerformers || tierPerformers.length === 0) {
      return;
    }

    if (startY > doc.internal.pageSize.getHeight() - 80) {
      doc.addPage();
      startY = 40;
    }

    doc.setFontSize(13);
    doc.text(`Sales Tier: ${tierName}`, 40, startY);
    startY += 10;

    const body = tierPerformers.map((p) => {
      const priorWeekTotalSales = p.location.eightWeekTotalSales ?? p.priorWeekSales ?? null;
      const priorWeekGfs = p.location.eightWeekTotalGfs ?? null;
      const priorWeekPercent = getEightWeekPercent(p.location);
      const tierAvgPercent = tierAveragePercent.get(tierName) ?? null;
      const priorWeekVariance =
        priorWeekPercent !== null && tierAvgPercent !== null ? priorWeekPercent - tierAvgPercent : null;

      return [
        p.rank,
        p.location.location,
        p.location.group ?? '--',
        priorWeekTotalSales ? formatCurrency(priorWeekTotalSales) : '--',
        priorWeekGfs ? formatCurrency(priorWeekGfs) : '--',
        priorWeekPercent !== null ? formatPercent(priorWeekPercent) : '--',
        priorWeekVariance !== null ? formatPercentWithSign(priorWeekVariance) : '--',
        p.location.weeklySalesToast !== null && p.location.weeklySalesToast !== undefined
          ? formatCurrency(p.location.weeklySalesToast)
          : '--',
        p.location.weeklyCatering !== null && p.location.weeklyCatering !== undefined
          ? formatCurrency(p.location.weeklyCatering)
          : '--',
        formatCurrency(p.location.weeklyTotal),
        p.location.weeklyGfs !== null && p.location.weeklyGfs !== undefined
          ? formatCurrency(p.location.weeklyGfs)
          : '--',
        formatPercent(p.location.gfsPercent),
        formatPercentWithSign(p.location.varianceFromAvg ?? 0),
      ];
    });

    autoTable(doc, {
      head: [headers],
      body,
      startY,
      styles: {
        fontSize: 9,
        cellPadding: 4,
      },
      headStyles: {
        fillColor: [122, 31, 42],
        textColor: 255,
      },
      margin: { left: 40, right: 40 },
      theme: 'grid',
    });

    startY = (doc as any).lastAutoTable.finalY + 20;

    if (idx < tierOrder.length - 1) {
      doc.setFontSize(10);
      doc.text('', 40, startY);
    }
  });

  doc.save(filename);
}

// Backwards compatibility: old name used CSV export; now it generates the PDF report
export const exportBottomPerformersToCSV = exportBottomPerformersToPDF;

